import { Flex } from '@sequencehq/core-components'
import { ReactNode } from 'react'
import { AccountInformationSection } from './sections/AccountInformationSection'
import { BillingInformationSection } from './sections/BillingInformationSection'
import { ContactsSection } from './sections/ContactsSection'
import { OtherInformationSection } from './sections/OtherInformationSection'
import { TaxSection } from './sections/TaxSection'
import { CustomerFormFieldsContext } from './useSectionExpansion'

/**
 * Layout shell for the customer form. Compose the sections you need as
 * children, wiring each to the field controls from `useCustomerFormFields` —
 * and mirror the rendered sections in the hook's `sectionValidation` config so
 * hidden fields never block submission.
 *
 * Pass `failedSubmitCount`, bumped on each failed submit, so every section
 * with errors opens up again even if the user had collapsed it.
 *
 * Give each section a section-scoped `data-testid` (e.g.
 * `createCustomerForm.accountInformation`); sections append only field-level
 * suffixes to it.
 *
 * ```tsx
 * const { fields, customProperties } = useCustomerFormFields({
 *   initialData,
 *   isANewCustomer: true
 * })
 *
 * <CustomerFormFields failedSubmitCount={failedSubmitCount}>
 *   <CustomerFormFields.AccountInformation
 *     data-testid="createCustomerForm.accountInformation"
 *     legalNameField={fields.legalName}
 *     contactsField={fields.contacts}
 *   />
 *   <CustomerFormFields.BillingInformation
 *     data-testid="createCustomerForm.billingInformation"
 *     addressFields={fields}
 *   />
 *   <CustomerFormFields.Tax
 *     data-testid="createCustomerForm.tax"
 *     taxStatusField={fields.taxStatus}
 *     taxRegistrationsField={fields.taxRegistrations}
 *   />
 * </CustomerFormFields>
 * ```
 */
const CustomerFormFieldsRoot = ({
  children,
  failedSubmitCount,
  'data-testid': testId
}: {
  children: ReactNode
  failedSubmitCount?: number
  'data-testid'?: string
}) => (
  <CustomerFormFieldsContext.Provider value={{ failedSubmitCount }}>
    <Flex direction="column" gap="20px" data-testid={testId}>
      {children}
    </Flex>
  </CustomerFormFieldsContext.Provider>
)

export const CustomerFormFields = Object.assign(CustomerFormFieldsRoot, {
  AccountInformation: AccountInformationSection,
  Contacts: ContactsSection,
  BillingInformation: BillingInformationSection,
  Tax: TaxSection,
  OtherInformation: OtherInformationSection
})
